import {Fragment, useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type {DeploymentHistoryResponse, DeploymentSnapshot} from "@calendall/shared";
import {ApiError} from "../api/apiRequest";
import {getDeployments, rollBack} from "../api/deploymentsApi";
import {PageHeader} from "../components/ui/PageHeader";
import {MonoText, StatusChip} from "../components/ui/StatusChip";
import {colors} from "../theme/tokens";

type RollbackOutcome = {severity: "success" | "error"; message: string};

/** apiRequest replaces the server's message with a generic one per status; the rollback function's own text is more useful. */
function messageOf(error: unknown): string {
    if (error instanceof ApiError && typeof error.details === "object" && error.details !== null && "message" in error.details) {
        return String(error.details.message);
    }
    return error instanceof Error ? error.message : String(error);
}

function Components({entries}: {entries: {name: string; value: string}[]}) {
    return (
            <Box sx={{display: "grid", gridTemplateColumns: "max-content max-content", columnGap: 1, rowGap: 0.25}}>
                {entries.map((entry) => (
                        <Fragment key={entry.name}>
                            <MonoText muted>{entry.name}</MonoText>
                            <MonoText>{entry.value}</MonoText>
                        </Fragment>
                ))}
            </Box>
    );
}

function SnapshotCells({snapshot}: {snapshot: DeploymentSnapshot}) {
    return (
            <>
                <TableCell sx={{verticalAlign: "top"}}>
                    <Components entries={snapshot.lambdaFunctions.map((lambda) => ({name: lambda.name, value: `v${lambda.version}`}))} />
                </TableCell>
                <TableCell sx={{verticalAlign: "top"}}>
                    {/* A deploy ID ends with its frontend's name, so the long router function name adds nothing here. */}
                    <Box sx={{display: "flex", flexDirection: "column", rowGap: 0.25}}>
                        {snapshot.frontends.map((frontend) => <MonoText key={frontend.name}>{frontend.deployId}</MonoText>)}
                    </Box>
                </TableCell>
            </>
    );
}

/** Shows the live deployment and the earlier ones the rollback function can return to, and rolls back to one of them. */
export function DeploymentsPage() {
    const {t} = useTranslation();
    const [history, setHistory] = useState<DeploymentHistoryResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [confirmStepsBack, setConfirmStepsBack] = useState<number | null>(null);
    const [rollingBackStepsBack, setRollingBackStepsBack] = useState<number | null>(null);
    const [outcome, setOutcome] = useState<RollbackOutcome | null>(null);

    useEffect(() => {
        document.title = t("DEPLOYMENTS_PAGE_TITLE");
    }, [t]);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadFailed(false);
        try {
            setHistory(await getDeployments());
        } catch {
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const confirmRollback = async () => {
        const stepsBack = confirmStepsBack;
        if (stepsBack === null) return;
        setConfirmStepsBack(null);
        setRollingBackStepsBack(stepsBack);
        setOutcome(null);
        try {
            const response = await rollBack({stepsBack});
            setOutcome({severity: "success", message: response.message});
        } catch (error) {
            setOutcome({severity: "error", message: messageOf(error)});
        } finally {
            setRollingBackStepsBack(null);
        }
        await load();
    };

    const rollbackButton = (stepsBack: number, label: string) => (
            <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    sx={{whiteSpace: "nowrap"}}
                    disabled={rollingBackStepsBack !== null}
                    onClick={() => setConfirmStepsBack(stepsBack)}
            >
                {rollingBackStepsBack === stepsBack ? t("DEPLOYMENTS_ROLLING_BACK") : label}
            </Button>
    );

    return (
            <>
                <PageHeader title={t("NAV_DEPLOYMENTS")} subtitle={t("DEPLOYMENTS_SUBTITLE")} />

                {outcome?.severity === "success" && (
                        <Alert severity="success">
                            <strong>{t("DEPLOYMENTS_ROLLBACK_SUCCEEDED")}</strong>
                            <Box component="pre" sx={{m: 0, mt: 0.5, whiteSpace: "pre-wrap", fontSize: "12px"}}>{outcome.message}</Box>
                        </Alert>
                )}
                {outcome?.severity === "error" && (
                        <Alert severity="error">
                            <strong>{t("DEPLOYMENTS_ROLLBACK_FAILED")}</strong> {outcome.message}
                            <Box sx={{mt: 0.5}}>{t("DEPLOYMENTS_ROLLBACK_FAILED_HINT")}</Box>
                        </Alert>
                )}

                {loadFailed && !loading && (
                        <Alert
                                severity="error"
                                action={<Button color="inherit" size="small" onClick={() => void load()}>{t("DEPLOYMENTS_RETRY")}</Button>}
                        >
                            {t("DEPLOYMENTS_LOAD_FAILED")}
                            <Box sx={{mt: 1}}>{rollbackButton(1, t("DEPLOYMENTS_ROLL_BACK_PREVIOUS"))}</Box>
                        </Alert>
                )}

                {!loadFailed && (
                        <Paper variant="outlined" sx={{overflow: "hidden"}}>
                            <TableContainer>
                                <Table aria-label={t("DEPLOYMENTS_TABLE_ARIA")}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("DEPLOYMENTS_COLUMN_STATUS")}</TableCell>
                                            <TableCell>{t("DEPLOYMENTS_COLUMN_BACKEND")}</TableCell>
                                            <TableCell>{t("DEPLOYMENTS_COLUMN_FRONTENDS")}</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading && !history && (
                                                <TableRow>
                                                    <TableCell colSpan={4}><Skeleton height={24} /></TableCell>
                                                </TableRow>
                                        )}
                                        {history && (
                                                <TableRow>
                                                    <TableCell sx={{verticalAlign: "top"}}><StatusChip tone="success">{t("DEPLOYMENTS_CURRENT")}</StatusChip></TableCell>
                                                    <SnapshotCells snapshot={history.current} />
                                                    <TableCell />
                                                </TableRow>
                                        )}
                                        {history?.history.map((snapshot) => (
                                                <TableRow key={snapshot.stepsBack}>
                                                    <TableCell sx={{verticalAlign: "top"}}>
                                                        <StatusChip tone="neutral">{t("DEPLOYMENTS_STEPS_BACK", {count: snapshot.stepsBack})}</StatusChip>
                                                    </TableCell>
                                                    <SnapshotCells snapshot={snapshot} />
                                                    <TableCell sx={{verticalAlign: "top"}} align="right">{rollbackButton(snapshot.stepsBack, t("DEPLOYMENTS_ROLL_BACK"))}</TableCell>
                                                </TableRow>
                                        ))}
                                        {history && history.history.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{py: 4, color: colors.muted}}>{t("DEPLOYMENTS_NO_HISTORY")}</TableCell>
                                                </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                )}

                <Dialog open={confirmStepsBack !== null} onClose={() => setConfirmStepsBack(null)}>
                    <DialogTitle>{t("DEPLOYMENTS_CONFIRM_TITLE", {count: confirmStepsBack ?? 1})}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>{t("DEPLOYMENTS_CONFIRM_BODY")}</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setConfirmStepsBack(null)}>{t("CANCEL")}</Button>
                        <Button variant="contained" color="error" onClick={() => void confirmRollback()}>{t("DEPLOYMENTS_ROLL_BACK")}</Button>
                    </DialogActions>
                </Dialog>
            </>
    );
}
